import { z } from 'zod';

/**
 * 会话四阶段快照 —— append-only 模型的核心(契约见 docs/remote-handoff-v2.md §3.1)。
 *
 * 一个会话(sessionId)生命周期内产生 1~4 条自包含全量快照:
 *   edge(HTML 请求到边缘) → page_view(JS 启动) → verify(定案) → page_hide(离开补发)
 * "当前状态" = 同 sessionId 中 ver 最大的快照。local 后端写时合并(upsert 带 ver 守卫),
 * remote 后端读时合并(ClickHouse ReplacingMergeTree(ver)),对外语义等价。
 */
export const sessionStageSchema = z.enum(['edge', 'page_view', 'verify', 'page_hide']);
export type SessionStage = z.infer<typeof sessionStageSchema>;

/**
 * 阶段序:同为非定案快照时阶段靠后者胜,使排序不依赖跨节点毫秒级时钟一致性
 * (edge 与 page_view 由不同执行环境打时间戳)。verify 由 finalized 位恒胜。
 */
export const STAGE_RANK: Record<SessionStage, number> = {
  edge: 0,
  page_view: 1,
  page_hide: 2,
  verify: 3,
};

/** 唯一的定案阶段。 */
export function isFinalizedStage(stage: SessionStage): boolean {
  return stage === 'verify';
}

const FINALIZED_BIT = 2 ** 48;
const STAGE_RANK_UNIT = 2 ** 46;

/**
 * ver = finalized × 2^48 + stageRank × 2^46 + emittedAt
 *
 * 把"已定案不被迟到快照覆盖"与"信息更全的阶段优先"编码成纯数学:
 * - finalized 位(2^48)保证 verify 快照恒大于任何非定案快照;
 * - stageRank 位(2^46)保证非定案阶段间的序不受时钟偏差影响;
 * - emittedAt(< 2^46 直到公元 4200+ 年)做同阶段内的 last-writer-wins。
 * 结果 < 2^53,JS number 精确,ClickHouse 存 UInt64。
 */
export function computeVer(stage: SessionStage, emittedAt: number): number {
  return (
    (isFinalizedStage(stage) ? FINALIZED_BIT : 0) + STAGE_RANK[stage] * STAGE_RANK_UNIT + emittedAt
  );
}
